/**
 * The dm.doc doors as the web reads them (W3-B D1 doors, D3 UI): the vocabularies the doors
 * validate against (pinned to the api source by `dmDocWire.test.ts`) and defensive readers.
 * A stored field off today's contract is served as-is, so every field is read by type and a
 * wrong-typed one renders as absent (the door's `diagnostics` names it; only the count shows).
 */

export const DM_DOCS_PATH = '/dm/docs';
export const DM_DOCS_SEARCH_PATH = '/dm/docs/search';

export const DM_DOC_STATUS_VALUES = ['draft', 'active', 'retired'] as const;
export const DM_DOC_KIND_VALUES = ['guide', 'recipe', 'reference', 'faq'] as const;
export const DM_DOC_AUDIENCE_VALUES = ['agent', 'human', 'all'] as const;
export const DM_DOC_REF_TYPE_VALUES = ['verb', 'type', 'setting', 'policy', 'hook', 'gate'] as const;
export const DM_DOC_KEY_MAX = 64;
export const DM_DOC_KEY_PATTERN = /^[a-z0-9][a-z0-9-]*$/;
export const DM_DOCS_SEARCH_QUERY_MAX = 500;

export type DmDocStatus = (typeof DM_DOC_STATUS_VALUES)[number];
export type DmDocKind = (typeof DM_DOC_KIND_VALUES)[number];
export type DmDocAudience = (typeof DM_DOC_AUDIENCE_VALUES)[number];
export type DmDocRefType = (typeof DM_DOC_REF_TYPE_VALUES)[number];

/** The search door's default lifecycle state; the UI sends it explicitly rather than relying on it. */
export const DM_DOCS_SEARCH_DEFAULT_STATUS: DmDocStatus = 'active';

export function isDmDocKey(raw: string): boolean {
  return raw.length <= DM_DOC_KEY_MAX && DM_DOC_KEY_PATTERN.test(raw);
}

/** The get tool's `key` arm (`GET /docs/by-key/:key`). */
export function dmDocByKeyPath(key: string): string {
  return `${DM_DOCS_PATH}/by-key/${encodeURIComponent(key)}`;
}

/** The search door's query rule (trimmed, bounded, one letter or digit); `null` when it would be accepted. */
export function dmDocsQueryProblem(raw: string): string | null {
  const text = raw.trim();
  if (text === '') return 'Type something to search.';
  if (text.length > DM_DOCS_SEARCH_QUERY_MAX) return `A search is at most ${DM_DOCS_SEARCH_QUERY_MAX} characters.`;
  if (!/[\p{L}\p{N}]/u.test(text)) return 'A search needs a letter or a digit.';
  return null;
}

export interface DmDocRef {
  type: string;
  key: string;
}

export interface DmDocRow {
  itemId: number;
  itemNo: number;
  title: string;
  status: string;
  key: string | null;
  kind: string | null;
  audience: string | null;
  summary: string | null;
  verifiedAt: string | null;
  moduleRef: string | null;
  refs: DmDocRef[];
  /** Stored fields the current contract does not admit (count only is rendered). */
  diagnosticCount: number;
}

export interface DmDocReference {
  edgeId: number;
  itemId: number;
  itemNo: number;
  type: string;
  title: string;
  status: string;
}

export interface DmDocDetail {
  doc: DmDocRow & { body: string | null };
  references: DmDocReference[];
  referencesTruncated: boolean;
}

export interface DmDocPage {
  rows: DmDocRow[];
  hasMore: boolean;
  /** Rows the door sent, dropped ones included — the paging cursor's step. */
  receivedCount: number;
}

type Bag = Record<string, unknown>;

function bagOf(raw: unknown): Bag | null {
  return typeof raw === 'object' && raw !== null && !Array.isArray(raw) ? (raw as Bag) : null;
}

function envelopeData(res: unknown): Bag {
  return bagOf((res as { data?: { data?: unknown } } | undefined)?.data?.data) ?? {};
}

function str(raw: unknown): string | null {
  return typeof raw === 'string' && raw !== '' ? raw : null;
}

function positiveInt(raw: unknown): number | null {
  return typeof raw === 'number' && Number.isInteger(raw) && raw > 0 ? raw : null;
}

function readRefs(raw: unknown): DmDocRef[] {
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((entry) => {
    const bag = bagOf(entry);
    const type = bag === null ? null : str(bag.type);
    const key = bag === null ? null : str(bag.key);
    return type === null || key === null ? [] : [{ type, key }];
  });
}

function readRow(raw: unknown): DmDocRow | null {
  const bag = bagOf(raw);
  if (bag === null) return null;
  const itemId = positiveInt(bag.itemId);
  const itemNo = positiveInt(bag.itemNo);
  if (itemId === null || itemNo === null) return null;
  return {
    itemId,
    itemNo,
    title: typeof bag.title === 'string' ? bag.title : `#${itemNo}`,
    status: typeof bag.status === 'string' ? bag.status : '',
    key: str(bag.key),
    kind: str(bag.kind),
    audience: str(bag.audience),
    summary: str(bag.summary),
    verifiedAt: str(bag.verified_at),
    moduleRef: str(bag.module_ref),
    refs: readRefs(bag.refs),
    diagnosticCount: Array.isArray(bag.diagnostics) ? bag.diagnostics.length : 0,
  };
}

function readReference(raw: unknown): DmDocReference | null {
  const bag = bagOf(raw);
  if (bag === null) return null;
  const edgeId = positiveInt(bag.edgeId);
  const itemId = positiveInt(bag.itemId);
  const itemNo = positiveInt(bag.itemNo);
  if (edgeId === null || itemId === null || itemNo === null) return null;
  return {
    edgeId,
    itemId,
    itemNo,
    type: typeof bag.type === 'string' ? bag.type : '',
    title: typeof bag.title === 'string' ? bag.title : `#${itemNo}`,
    status: typeof bag.status === 'string' ? bag.status : '',
  };
}

function rowsOf<T>(raw: unknown, read: (entry: unknown) => T | null): T[] {
  return Array.isArray(raw)
    ? raw.flatMap((entry) => {
        const row = read(entry);
        return row === null ? [] : [row];
      })
    : [];
}

/** A list or search page (a search row is a list row plus `rank`, which the UI does not show). */
export function readDmDocPage(res: unknown): DmDocPage {
  const data = envelopeData(res);
  return {
    rows: rowsOf(data.rows, readRow),
    hasMore: data.hasMore === true,
    receivedCount: Array.isArray(data.rows) ? data.rows.length : 0,
  };
}

/** `null` when the answer carries no readable doc. */
export function readDmDocDetail(res: unknown): DmDocDetail | null {
  const data = envelopeData(res);
  const docBag = bagOf(data.doc);
  const row = readRow(docBag);
  if (row === null || docBag === null) return null;
  return {
    doc: { ...row, body: typeof docBag.body === 'string' ? docBag.body : null },
    references: rowsOf(data.references, readReference),
    referencesTruncated: data.referencesTruncated === true,
  };
}
